import { useMemo } from 'react';
import { FilterToolbarItemId } from 'src/components/Filters';
import { useInternalNotesFilter } from 'src/components/Filters/InternalNotesFilter';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  UIReportV2PurchaseQuery,
} from 'src/WebApiController';

import { usePurchaseCurrencyCodesFilters } from './Filters/usePurchaseCurrencyCodesFilters';
import { usePurchaseOrderStateFilters } from './Filters/usePurchaseOrderStateFilters';
import { usePurchasePaymentMethodFilters } from './Filters/usePurchasePaymentMethodFilters';
import { usePurchasePaymentStatusFilters } from './Filters/usePurchasePaymentStatusFilters';
import { useMergedTagFilters } from './Tags/useMergedTagFilters';
import { usePurchaseInfoFilters } from './usePurchaseInfoFilters';

export const soldTicketReportMandatoryFiltersToShow: FilterToolbarItemId[] = [
  'eventDates',
  'saleStatuses',
];

const PurchaseInfoFilterIdsToShow: FilterToolbarItemId[] = [
  'purchaseVendorId',
  'purchaseVendorAccountIds',
  'purchaseBuyerId',
  'purchaseDates',
  'purchaseOrderCreatedBy',
  'purchaseCancellationDates',
];

export function usePurchaseReportFilters({
  isHorizontalLayout,
}: {
  isHorizontalLayout?: boolean;
}) {
  const { initialQuery, tempQuery, setTempQuery } =
    useFilterQueryContext<UIReportV2PurchaseQuery>();

  const purchaseInfoFilters = usePurchaseInfoFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    showCancellationDateFilter: true,
    useRelativePresets: true,
  });

  const supportedPurchaseInfoFilters = useMemo(() => {
    const items = purchaseInfoFilters.items.filter((item) =>
      PurchaseInfoFilterIdsToShow.includes(item.filterId)
    );

    return {
      ...purchaseInfoFilters,
      items,
    };
  }, [purchaseInfoFilters]);

  const notesFilters = useInternalNotesFilter({
    entityType: ActionOutboxEntityType.Purchase,
    isHorizontalLayout,
  });

  const tagsFilter = useMergedTagFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    entityType: ActionOutboxEntityType.Purchase,
    includeInReport: true,
    includeEventTags: false,
  });

  const purchasePaymentStatusFilter = usePurchasePaymentStatusFilters({
    query: tempQuery,
    setQuery: setTempQuery,
  });

  const purchasePaymentMethodFilter = usePurchasePaymentMethodFilters({
    query: tempQuery,
    setQuery: setTempQuery,
  });

  const purchaseOrderStateFilter = usePurchaseOrderStateFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    isHorizontalLayout,
  });

  const purchaseCurrencyCodesFilter = usePurchaseCurrencyCodesFilters({
    query: tempQuery,
    setQuery: setTempQuery,
  });

  return {
    filters: [
      supportedPurchaseInfoFilters,
      {
        titleContentId: ContentId.Payments,
        type: 'row',
        items: [purchasePaymentStatusFilter, purchasePaymentMethodFilter],
      },
      {
        titleContentId: ContentId.Status,
        type: 'row',
        items: [purchaseOrderStateFilter],
      },
      {
        titleContentId: ContentId.Cost,
        type: 'row',
        items: [purchaseCurrencyCodesFilter],
      },
      ...(notesFilters ? [notesFilters] : []),
      ...tagsFilter,
    ],
  };
}
